/**
 * Slice 5 — Job evidence: Before · After · Keys · Maintenance (+ lightbox via SimplePhotoGrid).
 * Slice 20b — Check unit entry → UnitCheckView.
 */
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { SimplePhotoGrid } from "@/components/owner/SubmissionPhotoGallery";
import { useMaintenanceReportsForTask } from "@/hooks/useMaintenanceReportsForTask";
import { collectSimplePhotoUrls, readSimpleFlowState } from "@/lib/simpleTurnover";
import { isVideoMediaUrl } from "@/lib/mediaUrl";
import CleanerRatingCard from "@/components/owner/CleanerRatingCard";
import { ShareWithGuest } from "@/components/owner/ShareWithGuest";
import { LiveJobDesk } from "@/components/owner/LiveJobDesk";
import { useUnitItems } from "@/hooks/useUnitItems";
import { useUnitCheckMarks } from "@/hooks/useUnitCheckMarks";
import { TimeOnSiteSection, type TimeOnSiteTask } from "@/components/owner/TimeOnSiteSection";
import { ArrivalAnswersSection } from "@/components/owner/ArrivalAnswersSection";
import { deleteTestClean, testSetupLine } from "@/lib/testClean";
import { toast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import { sortRoomsWalkOrder } from "@/lib/roomCoverPhotos";
import type { RoomShots, RoomSkipReason } from "@/lib/simpleTurnover";

export interface JobEvidenceTask extends TimeOnSiteTask {
  id: string;
  checklist_completed?: boolean | null;
  inventory_completed?: boolean | null;
  rating?: number | null;
  rating_note?: string | null;
  rated_at?: string | null;
  closeout_keys_confirmed?: boolean | null;
  closeout_keys_photo_url?: string | null;
  closeout_notes?: string | null;
  entry_walkthrough_state?: unknown;
  /** Slice 50 — arrival checklist as it was when the job started. */
  arrival_tasks?: unknown;
  /** Slice 55 — a Test clean and its pretend turnover. */
  is_test?: boolean | null;
  test_setup?: unknown;
  /** Slice 56 — photos still on the cleaner's phone. */
  photos_pending?: number | null;
  duration_minutes?: number | null;
  completed_at?: string | null;
  cleaner_id?: string | null;
}

interface JobEvidenceViewProps {
  propertyId: string;
  propertyLabel: string;
  submittedAt: string;
  cleanerName: string;
  cleaningTask: JobEvidenceTask | null;
  cleaningTaskId: string | null;
  onBack: () => void;
  onGoToUnit?: () => void;
  /** Cleaner Past jobs: read-only evidence, no rating / share / unit check. */
  mode?: "owner" | "cleaner";
  /** Called after a Test clean is deleted so the list can close this view. */
  onTestDeleted?: () => void;
}

function toGridPhotos(urls: string[]) {
  return urls.filter(Boolean).map((photo_url, i) => ({
    id: `${i}`,
    photo_url,
  }));
}

export function JobEvidenceView({
  propertyId,
  propertyLabel,
  submittedAt,
  cleanerName,
  cleaningTask,
  cleaningTaskId,
  onBack,
  onGoToUnit,
  mode = "owner",
  onTestDeleted,
}: JobEvidenceViewProps) {
  const isCleaner = mode === "cleaner";
  const queryClient = useQueryClient();
  const isTest = !!cleaningTask?.is_test;
  // Slice 56: while photos are still on the cleaner's phone, check back quietly and fill them in.
  const { data: polledPending } = useQuery({
    queryKey: ["photos-pending", cleaningTaskId],
    queryFn: async () => {
      const { data } = await supabase.from("cleaning_tasks").select("photos_pending").eq("id", cleaningTaskId as string).maybeSingle();
      const n = (data as { photos_pending?: number | null } | null)?.photos_pending ?? 0;
      return n;
    },
    enabled: !!cleaningTaskId && !!cleaningTask?.photos_pending,
    refetchInterval: (q) => ((q.state.data ?? 1) > 0 ? 8000 : false),
    initialData: cleaningTask?.photos_pending ?? 0,
  });
  const photosPending = polledPending ?? cleaningTask?.photos_pending ?? 0;
  useEffect(() => {
    if (cleaningTask?.photos_pending && polledPending === 0) {
      void queryClient.invalidateQueries({ queryKey: ["cleaning-submissions"] });
      void queryClient.invalidateQueries({ queryKey: ["reports-completed-tasks", propertyId] });
    }
  }, [polledPending, cleaningTask?.photos_pending, queryClient, propertyId]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pretendLine = testSetupLine(cleaningTask?.test_setup);
  const confirmDeleteTest = async () => {
    if (!cleaningTaskId) return;
    setDeleting(true);
    try {
      await deleteTestClean(cleaningTaskId);
      setDeleteOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["cleaning-submissions"] }),
        queryClient.invalidateQueries({ queryKey: ["reports-completed-tasks", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["maintenance-reports", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["inventory-reorders"] }),
      ]);
      toast({ title: "Test clean deleted" });
      onTestDeleted?.();
    } catch (err) {
      toast({
        title: "Couldn’t delete the test clean",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };
  const [unitCheckOpen, setUnitCheckOpen] = useState(false);
  const { data: maintenanceReports = [] } = useMaintenanceReportsForTask(cleaningTaskId ?? undefined);
  const { data: unitItems = [] } = useUnitItems(propertyId);
  const { data: unitMarks = [] } = useUnitCheckMarks(cleaningTaskId ?? undefined);
  const simple = readSimpleFlowState(cleaningTask?.entry_walkthrough_state);
  const { data: roomRows = [] } = usePropertyRooms(propertyId);
  const { data: roomCover = {} } = useRoomCoverData(propertyId);
  const orderedRooms = sortRoomsWalkOrder(roomRows, roomCover);
  const beforeUrls = collectSimplePhotoUrls(simple, "before");
  const afterUrls = collectSimplePhotoUrls(simple, "after");
  const maintenanceCheckRows = Object.entries(simple.maintenance_checks ?? {}).map(
    ([id, row]) => ({
      id,
      title: row.title?.trim() || "Maintenance",
      status: row.status,
      proofUrl: row.proof_url || null,
    }),
  );
  const arrivalKeysUrl = simple.keys_photo_url || null;
  const dropKeysUrl =
    simple.keys_drop_photo_url ||
    // New dropoffs also write closeout; only treat as dropoff when drop field set OR arrival exists separately
    (simple.keys_photo_url
      ? cleaningTask?.closeout_keys_photo_url &&
        cleaningTask.closeout_keys_photo_url !== simple.keys_photo_url
        ? cleaningTask.closeout_keys_photo_url
        : null
      : cleaningTask?.closeout_keys_photo_url || null);
  const hasDropoff = !!dropKeysUrl;
  const _hasArrival = !!arrivalKeysUrl;
  void _hasArrival;

  const dateLabel = format(new Date(submittedAt), "MMM d");
  const durationLabel =
    cleaningTask?.duration_minutes != null
      ? `${Math.round(cleaningTask.duration_minutes)}m`
      : "—";

  const maintCount = maintenanceReports.length;
  const canRate =
    !isCleaner &&
    !!cleaningTask &&
    cleaningTask.checklist_completed === true &&
    cleaningTask.inventory_completed === true &&
    cleaningTask.rating == null;

  const unitCheckIncomplete =
    unitItems.length > 0 &&
    !!cleaningTaskId &&
    unitMarks.length < unitItems.length;

  if (unitCheckOpen && cleaningTaskId) {
    return (
      <LiveJobDesk
        propertyId={propertyId}
        cleaningTaskId={cleaningTaskId}
        cleanerId={cleaningTask?.cleaner_id ?? null}
        cleanerName={cleanerName}
        mode="post"
        onBack={() => setUnitCheckOpen(false)}
        onGoToUnit={() => {
          setUnitCheckOpen(false);
          onGoToUnit?.();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-tour="tour-job-evidence">
      {isCleaner ? (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-11 items-center rounded-xl border-[1.5px] border-[#e6edf1] bg-white px-3.5 text-[15px] font-bold text-[#475569]"
          >
            ← Back
          </button>
          <div className="min-w-0">
            <div className="truncate text-[17px] font-bold tracking-[-0.3px] text-[#0f1720]">
              {propertyLabel}
            </div>
            <div className="text-[13px] font-medium text-[#64748b]">
              Completed {format(new Date(submittedAt), "MMM d")}
            </div>
          </div>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex w-fit items-center gap-2 text-[13px] font-medium text-[#64748b] hover:text-[#24a89a]"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2.4} />
            Reports
          </button>

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-[30px] font-bold tracking-[-0.6px] text-[#0f1720] max-sm:text-[26px]">
                Job evidence
              </h2>
              <p className="text-[15px] font-medium text-[#64748b]">
                {propertyLabel} · {dateLabel}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {[
                { label: "BEFORE", value: String(beforeUrls.length) },
                { label: "AFTER", value: String(afterUrls.length) },
                { label: "TIME", value: durationLabel },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex min-w-[104px] flex-col gap-0.5 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-[18px] py-3"
                >
                  <div className="text-[11px] font-bold tracking-[0.5px] text-[#64748b]">{s.label}</div>
                  <div className="text-xl font-bold text-[#0f1720]">{s.value}</div>
                </div>
              ))}
              {cleaningTaskId ? (
                <button
                  type="button"
                  onClick={() => setUnitCheckOpen(true)}
                  className="flex h-[52px] items-center rounded-[14px] bg-[#24a89a] px-5 text-[15px] font-bold text-white hover:bg-[#1f958a]"
                >
                  Check unit
                </button>
              ) : null}
            </div>
          </div>
        </>
      )}

      {!isCleaner && unitCheckIncomplete ? (
        <div className="inline-flex w-fit rounded-full border-[1.5px] border-[#f1ddc2] bg-[#fdf3e8] px-3 py-1.5 text-xs font-bold text-[#b45309]">
          Unchecked
        </div>
      ) : null}

      {photosPending > 0 && !isCleaner ? (
        <div className="flex flex-col gap-1.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-[18px] py-3.5">
          <span className="inline-flex w-fit rounded-full bg-[#eef2f5] px-3 py-1 text-[12.5px] font-bold text-[#64748b]">
            Waiting to send from the cleaner’s phone
          </span>
          <div className="text-[14px] font-medium leading-snug text-[#475569]">
            {photosPending} photo{photosPending === 1 ? " is" : "s are"} still on the cleaner’s phone. They appear here on their own when it’s back in signal.
          </div>
        </div>
      ) : null}

      {isTest ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="inline-flex h-[26px] items-center rounded-full bg-[#0f1720] px-3 text-[12.5px] font-bold text-white">
              Test clean{(cleaningTask?.test_setup as { ended_early?: boolean } | null)?.ended_early ? " · ended early" : ""}
            </span>
            <span className="text-[14px] font-medium text-[#475569]">Not counted in stats or billing.</span>
          </div>
          {pretendLine ? (
            <div className="flex flex-col gap-1 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-[18px] py-3.5">
              <div className="text-[13px] font-bold text-[#64748b]">Pretend turnover</div>
              <div className="text-[15px] font-bold leading-snug text-[#0f1720]">{pretendLine}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      {!isCleaner ? <TimeOnSiteSection propertyId={propertyId} task={cleaningTask} /> : null}

      <ArrivalAnswersSection
        arrivalTasks={cleaningTask?.arrival_tasks}
        entryWalkthroughState={cleaningTask?.entry_walkthrough_state}
      />

      {simple.walk_by_room || simple.after_by_room ? (
        <div className="flex flex-col gap-6">
          <RoomEvidence title="Before" by={simple.walk_by_room} rooms={orderedRooms} />
          <RoomEvidence title="After" by={simple.after_by_room} rooms={orderedRooms} walkBy={simple.walk_by_room} />
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          <EvidenceGallery title="Before" urls={beforeUrls} />
          <EvidenceGallery title="After" urls={afterUrls} />
        </div>
      )}

      {maintenanceCheckRows.length > 0 ? (
        <div className="flex flex-col gap-3">
          <div className="text-xs font-bold tracking-[0.8px] text-[#64748b]">MAINTENANCE</div>
          <div className="flex flex-col gap-2.5">
            {maintenanceCheckRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-3"
              >
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-[10px] bg-[#eff4f7]">
                  {row.proofUrl ? (
                    <img src={row.proofUrl} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-bold text-[#0f1720]">{row.title}</div>
                  <div
                    className={cn(
                      "text-[13px] font-bold",
                      row.status === "done" ? "text-[#15776d]" : "text-[#8494a2]",
                    )}
                  >
                    {row.status === "done" ? "Done" : "Skipped"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs font-bold tracking-[0.8px] text-[#64748b]">KEYS</div>
          {hasDropoff ? (
            <div className="flex items-center gap-2 rounded-full bg-[#e2eeed] px-3.5 py-1.5 text-[13px] font-bold text-[#15776d]">
              <CheckIcon />
              Left in spot
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-full bg-[#fdf3e5] px-3.5 py-1.5 text-[13px] font-bold text-[#b45309]">
              No dropoff photo
            </div>
          )}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <KeysEvidenceCard
            title="Arrival keys"
            url={arrivalKeysUrl}
            emptyLabel="No arrival photo"
            accent={false}
          />
          <KeysEvidenceCard
            title="Keys left"
            url={dropKeysUrl}
            emptyLabel="No dropoff photo"
            accent
            badge="Dropoff"
            missing={!hasDropoff}
          />
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-[18px]">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xl font-bold tracking-[-0.3px] text-[#0f1720]">Maintenance</div>
            {maintCount > 0 && (
              <div className="rounded-full bg-[#fdeee1] px-3 py-1 text-xs font-bold text-[#96501d]">
                {maintCount} flag{maintCount === 1 ? "" : "s"}
              </div>
            )}
          </div>
          {maintCount === 0 ? (
            <div className="flex h-[130px] items-center justify-center rounded-[13px] border-[1.5px] border-dashed border-[#e6edf1] bg-[#f4f7f9] text-sm font-medium text-[#8494a2]">
              No flags
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {maintenanceReports.map((r) => {
                const photos = (r.photos || []).filter(Boolean).map((photo_url, i) => ({
                  id: `${r.id}-${i}`,
                  photo_url,
                }));
                return (
                  <div key={r.id} className="flex gap-3.5">
                    {photos.length > 0 ? (
                      <div className="shrink-0 [&_button>div]:!h-[130px] [&_button>div]:!w-[130px]">
                        <SimplePhotoGrid photos={photos.slice(0, 1)} />
                      </div>
                    ) : (
                      <div className="h-[130px] w-[130px] shrink-0 rounded-[13px] bg-[#eff4f7]" />
                    )}
                    <div className="flex min-w-0 flex-col gap-1.5 pt-0.5">
                      <div className="text-[17px] font-bold text-[#0f1720]">
                        {r.notes?.trim() || r.task_description || "Issue"}
                      </div>
                      <div className="text-sm font-medium text-[#64748b]">
                        {r.room_name}
                        {r.created_at
                          ? ` · ${format(new Date(r.created_at), "HH:mm")}`
                          : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {cleaningTaskId && !isCleaner && !isTest && (
        <div className="flex flex-col gap-4 border-t border-[#e6edf1] pt-4">
          <CleanerRatingCard
            taskId={cleaningTaskId}
            cleanerName={cleanerName}
            existingRating={cleaningTask?.rating ?? null}
            existingNote={cleaningTask?.rating_note ?? null}
            ratedAt={cleaningTask?.rated_at ?? null}
            canRate={canRate}
          />
          <ShareWithGuest cleaningTaskId={cleaningTaskId} propertyId={propertyId} compact />
        </div>
      )}

      {isTest && cleaningTaskId && !isCleaner ? (
        <div className="flex flex-wrap items-center gap-4 border-t-[1.5px] border-[#e6edf1] pt-4">
          <div className="min-w-0 flex-1 text-[14px] font-medium text-[#64748b]">
            Delete removes the report, its photos, and its lines in Issues and the buy list.
          </div>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="flex h-[52px] items-center rounded-[14px] border-[1.5px] border-[#efc6c0] bg-white px-[22px] text-[16px] font-bold text-[#b4453a] hover:bg-[#fdf1ef]"
          >
            Delete test clean
          </button>
        </div>
      ) : null}

      {deleteOpen ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-[rgba(15,23,32,0.45)] sm:items-center">
          <div className="flex w-full flex-col gap-3.5 rounded-t-[22px] bg-white p-6 shadow-[0_24px_60px_rgba(15,23,32,0.25)] sm:w-[460px] sm:rounded-[22px]">
            <div className="text-[23px] font-bold tracking-[-0.4px]">Delete this test clean?</div>
            <div className="text-[15.5px] font-medium leading-relaxed text-[#475569]">
              Its photos, counts and problems go too, including the ones in Issues and the buy list. Real stats and billing were never touched.
            </div>
            <div className="mt-1.5 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteOpen(false)}
                className="flex h-[52px] items-center justify-center rounded-[14px] border-[1.5px] border-[#d7e0e6] px-[22px] text-[16px] font-bold"
              >
                Keep it
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void confirmDeleteTest()}
                className="flex h-[52px] items-center justify-center rounded-[14px] bg-[#b4453a] px-[22px] text-[16px] font-bold text-white"
              >
                {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Delete test clean"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}


function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function KeysEvidenceCard({
  title,
  url,
  emptyLabel,
  accent,
  badge,
  missing,
}: {
  title: string;
  url: string | null;
  emptyLabel: string;
  accent?: boolean;
  badge?: string;
  missing?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-[18px] bg-white p-4",
        missing
          ? "border-2 border-dashed border-[#e4c99b] bg-[#fffaf2]"
          : accent
            ? "border-2 border-[#24a89a]"
            : "border-[1.5px] border-[#e6edf1]",
      )}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            "text-[18px] font-bold tracking-[-0.2px]",
            missing ? "text-[#8a5a10]" : accent ? "text-[#0f1720]" : "text-[#0f1720]",
          )}
        >
          {title}
        </div>
        {badge ? (
          <div
            className={cn(
              "rounded-full px-2.5 py-1 text-xs font-bold",
              missing ? "bg-[#fdf3e5] text-[#b45309]" : "bg-[#e2eeed] text-[#15776d]",
            )}
          >
            {badge}
          </div>
        ) : null}
      </div>
      {url ? (
        <div className="[&_button>div]:!h-[200px] [&_button>div]:!w-full [&_button>div]:!max-w-none [&_button]:!w-full">
          <SimplePhotoGrid photos={toGridPhotos([url])} className="!flex-wrap" />
        </div>
      ) : (
        <div
          className={cn(
            "flex h-[200px] flex-col items-center justify-center gap-2.5 rounded-[14px] text-[16px] font-bold",
            missing
              ? "border-[1.5px] border-[#f0e0c6] bg-[#fdf7ee] text-[#b45309]"
              : "border-[1.5px] border-dashed border-[#e6edf1] bg-[#f4f7f9] text-[#8494a2]",
          )}
        >
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

const SKIP_REASON_TEXT: Record<RoomSkipReason, string> = {
  locked: "Door is locked",
  guest: "Guest is still inside",
  unsafe: "Not safe to go in",
};

/** Slice 53 — Before / After photos grouped by room, with any skipped room and why. */
function RoomEvidence({
  title,
  by,
  rooms,
  walkBy,
}: {
  title: string;
  by?: Record<string, RoomShots>;
  rooms: { id: string; name: string }[];
  /** After skips whatever Walk skipped. */
  walkBy?: Record<string, RoomShots>;
}) {
  if (!by && !walkBy) return null;
  const known = new Set(rooms.map((r) => r.id));
  const extra = Object.keys({ ...(walkBy ?? {}), ...(by ?? {}) })
    .filter((id) => !known.has(id))
    .map((id) => ({ id, name: "Room" }));
  const all = [...rooms, ...extra];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold tracking-[0.8px] text-[#64748b]">{title.toUpperCase()} · BY ROOM</div>
      <div className="grid gap-5 md:grid-cols-2">
        {all.map((room) => {
          const shots = by?.[room.id];
          const skip = shots?.skip ?? walkBy?.[room.id]?.skip;
          if (skip) {
            return (
              <div
                key={room.id}
                className="flex flex-col gap-1 rounded-[18px] border-[1.5px] border-[#f1ddc2] bg-[#fdf3e8] p-[18px]"
              >
                <div className="text-xl font-bold tracking-[-0.3px] text-[#0f1720]">{room.name}</div>
                <div className="text-sm font-bold text-[#b45309]">Skipped · {SKIP_REASON_TEXT[skip]}</div>
              </div>
            );
          }
          if (!shots || shots.photo_urls.length === 0) return null;
          return <EvidenceGallery key={room.id} title={room.name} urls={shots.photo_urls} />;
        })}
      </div>
    </div>
  );
}

function EvidenceGallery({ title, urls }: { title: string; urls: string[] }) {
  const photos = toGridPhotos(urls);
  const videoCount = urls.filter((u) => isVideoMediaUrl(u)).length;
  const countLabel =
    videoCount > 0 && videoCount < urls.length
      ? `${urls.length} media`
      : videoCount > 0
        ? `${urls.length} video${urls.length === 1 ? "" : "s"}`
        : `${urls.length} photo${urls.length === 1 ? "" : "s"}`;
  return (
    <div className="flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-[18px]">
      <div className="flex items-center justify-between">
        <div className="text-xl font-bold tracking-[-0.3px] text-[#0f1720]">{title}</div>
        <div className="text-sm font-bold text-[#24a89a]">{countLabel}</div>
      </div>
      {photos.length === 0 ? (
        <div className="flex aspect-[3/1] items-center justify-center rounded-[13px] border-[1.5px] border-dashed border-[#e6edf1] bg-[#f4f7f9] text-sm font-medium text-[#8494a2]">
          No media
        </div>
      ) : (
        <div
          className={cn(
            "[&_button]:!flex-none",
            "[&_button>div]:!h-auto [&_button>div]:!w-full [&_button>div]:!aspect-square [&_button>div]:!rounded-[13px]",
            "[&_.flex]:!grid [&_.flex]:!grid-cols-3 [&_.flex]:!gap-2.5 [&_.flex]:!overflow-visible",
          )}
        >
          <SimplePhotoGrid photos={photos} />
        </div>
      )}
    </div>
  );
}
